import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { isEqual, uniq } from "lodash"

import {
  Checkbox,
  FormControlLabel,
  Grid,
  SelectChangeEvent,
  Skeleton,
  useMediaQuery,
  useTheme,
} from "../../../../../components/mui.components"
import { useAppSelector } from "../../../../../core/app/hooks"
import {
  OrderFilterQueryParams,
  PatientFilterQueryParams,
} from "../../../../../core/app/slices/patients/patient.types"
import { RootState } from "../../../../../core/app/store"
import { ORDER_LIST_STATUS } from "../../../config/status.config"
import { STATUS_UPDATED } from "../../filter"

import UStatusFilter from "./UStatusFilter"
import { UTableFilterList } from "./UTableFilterList"
import UTableSearch from "./UTableSearch"
import { getOrderTypeFilterList } from "@/core/app/slices/order/orderThunkApi"

const UTableFilter: FC<{
  onFilterUpdate?: (
    fields: PatientFilterQueryParams | OrderFilterQueryParams,
  ) => void
  filterQuery: any
  onSearch?: (val: string) => void
  searchText?: string
  enableSearch?: boolean
  isOrderList?: boolean
  name?: string
}> = ({
  onFilterUpdate,
  filterQuery,
  onSearch,
  searchText,
  enableSearch,
  isOrderList,
  name,
}) => {
  const { doctorList } = useAppSelector(
    (state: RootState) => state.doctorService,
  )

  const { workflow, caseDispositionList=[] } = useAppSelector((state:RootState)=> state.userService)
  const { orderTypeFilterList } = useAppSelector((state:RootState)=> state.orderService)

  const dispatch = useDispatch()
  const theme = useTheme()
  const isXs = useMediaQuery(theme.breakpoints.down("sm"))

  const [filterQueryParams, setFilterQueryParams] =
    useState<PatientFilterQueryParams>({})
  const [orderFilterQueryParams, setOrderFilterQueryParams] =
    useState<OrderFilterQueryParams>({})
  const { t } = useTranslation()
  const [isShowArchivedPatients, setIsShowArchivedPatients] = useState(false)
  const [statusUpdatedFilter, setStatusUpdatedFilter] = useState("")
  const [orderDateFilter, setOrderDateFilter] = useState("")
  const [selectedOrderType, setSelectedOrderType] = useState([])
  const [assigners, setAssigners] = useState([])
  const [status, setStatus] = useState<any[]>([])
  const [selectedAssigners, setSelectedAssigners] = useState<
    (string | number)[]
  >([])
  const [selectedStatus, setSelectedStatus] = useState<string[]>([])
  const [selectedOrderStatus, setSelectedOrderStatus] = useState<string[]>([])

  const [workflowFilter, setWorflowFilter] = useState(null)
  // const [orderTypeFilterList, setOrderTypeFilterList] = useState<any[]>([])

  useEffect(() => {
    if (filterQuery) {
      if (isOrderList) {
        handleOrderList(filterQuery)
      } else {
        handlePatientList(filterQuery)
      }
    }
  }, [filterQuery])

  const handleOrderList = (filterQuery) => {
    setOrderFilterQueryParams(filterQuery)
    setSelectedOrderStatus(
      filterQuery.status_filter?.length ? [...filterQuery.status_filter] : [],
    )
    setSelectedOrderType( (filterQuery?.bundle_filter?.length) ? filterQuery.bundle_filter : [])
    setOrderDateFilter(filterQuery.date_filter)
  }

  const handlePatientList = (filterQuery) => {
    setFilterQueryParams(filterQuery)
    setSelectedStatus(filterQuery.status?.length ? [...filterQuery.status] : [])
    setSelectedAssigners(
      filterQuery.assigned_to?.length ? [...filterQuery.assigned_to] : [],
    )
    setIsShowArchivedPatients(filterQuery.show_archived_patients === 1)
    setStatusUpdatedFilter(filterQuery.status_updated)
    setWorflowFilter(filterQuery?.phase)
  }

  useEffect(() => {
    if (!isShowArchivedPatients) {
      const status = caseDispositionList.filter(
        (status) => !status.isDisableDropdownShow,
      )
      setStatus(status)
    } else {
      setStatus(caseDispositionList)
    }
  }, [dispatch, caseDispositionList, isShowArchivedPatients])

  useEffect(() => {
    setAssigners(doctorList)
  }, [doctorList])

  useEffect(() => {
    if (isOrderList) {
      if (
        !isEqual(orderFilterQueryParams, filterQuery) &&
        Object.keys(orderFilterQueryParams).length
      ) {
        onFilterUpdate({
          ...orderFilterQueryParams,
        })
      }
    } else {
      if (
        !isEqual(filterQueryParams, filterQuery) &&
        Object.keys(filterQueryParams).length
      ) {
        onFilterUpdate({
          ...filterQueryParams,
        })
      }
    }
  }, [filterQueryParams, orderFilterQueryParams])

  useEffect(()=>{
    if(enableSearch && name === "Active orders" && !orderTypeFilterList.length){
      // Fetch Orders List
      dispatch(getOrderTypeFilterList())
    }
  },[name])

  const handleChange = (event: SelectChangeEvent, filterType: string) => {
    const updatedParams = { ...filterQueryParams }
    const updateOrderParams = { ...orderFilterQueryParams }
    if (filterType === "status") {
      const selectedStatuses = uniq(event.target.value)
      setSelectedStatus(selectedStatuses)
      updatedParams.status = selectedStatuses
    } else if (filterType === "assignedTo") {
      const selectedAssignees = uniq(event.target.value)
      setSelectedAssigners(selectedAssignees)
      updatedParams.assigned_to = selectedAssignees
    } else if (filterType === "updatedtime") {
      const updatedTime = event.target.value
      setStatusUpdatedFilter(updatedTime)
      updatedParams.status_updated = updatedTime
    } else if (filterType === "order_type") {
      const orderType = uniq(event.target.value)
      setSelectedOrderType(orderType)
      updateOrderParams.bundle_filter = orderType
    } else if (filterType === "orderStatus") {
      const selectedOrderStatus = uniq(event.target.value)
      setSelectedOrderStatus(selectedOrderStatus)
      updateOrderParams.status_filter = selectedOrderStatus
    } else if (filterType === "orderDate") {
      const orderDate = event.target.value
      setOrderDateFilter(orderDate)
      updateOrderParams.date_filter = orderDate
    } else if (filterType === "workflow") {
      setWorflowFilter(event.target.value)
      updatedParams.phase = Number(event.target.value)
    }
    setFilterQueryParams(updatedParams)
    setOrderFilterQueryParams(updateOrderParams)
  }

  const handleCheckboxChange = () => {
    const showArchived = !isShowArchivedPatients
    setIsShowArchivedPatients(showArchived)
  
  
    const updatedParams = {
      ...filterQueryParams,
      show_archived_patients: showArchived ? 1 : 0,
    }
    setFilterQueryParams(updatedParams)
  }

  const renderStatusUpdatedValue = () => {
    if (!isOrderList) {
      return !statusUpdatedFilter
        ? t("filter.statusUpdated")
        : STATUS_UPDATED.find((s) => s.key === statusUpdatedFilter)?.displayText
    } else {
      return !orderDateFilter
        ? "Order Date"
        : STATUS_UPDATED.find((s) => s.key === orderDateFilter)?.displayText
    }
  }

  return (
    <Grid
      container
      columnGap={1}
      flexDirection={"row"}
      alignItems={"center"}
      flexWrap={"nowrap"}
      pt={"5px"}
    >
      {enableSearch && name === "Active orders" && (
        <>
          <Grid item sx={{ minWidth: "702px" }}>
            <UTableSearch onSearch={onSearch} searchText={searchText} />
          </Grid>
          <Grid item xs={isXs ? 12 : undefined}>
            <UStatusFilter
              statusUpdatedFilter={orderDateFilter}
              dateFilterOptions={STATUS_UPDATED}
              onFilterChange={(e) => handleChange(e, "orderDate")}
              renderValue={renderStatusUpdatedValue}
            />
          </Grid>
          {orderTypeFilterList.length ? (
            <Grid item xs={isXs ? 12 : undefined}>
              <UTableFilterList
                selectedMenu={selectedOrderType}
                menuList={orderTypeFilterList}
                onFilterChange={handleChange}
                listStyle={{
                  minWidth: "269px",
                }}
                placeHolder="Order Type"
                filterKey="order_type"
                label="Order Type"
              />
            </Grid>
          ): (
            <Grid item xs={isXs ? 12 : undefined} sx={{opacity: "0.35"}}>
              <Skeleton variant="rounded" width={"272px"} height={"40px"} />
            </Grid>
          )}
          <Grid item xs={isXs ? 12 : undefined}>
            <UTableFilterList
              selectedMenu={selectedOrderStatus}
              listStyle={{
                minWidth: "269px",
              }}
              menuList={ORDER_LIST_STATUS}
              onFilterChange={handleChange}
              placeHolder="Status"
              filterKey="orderStatus"
            />
          </Grid>
          
        </>
      )}
      {enableSearch && name === "Patient List" && (
        <>
          <Grid
            item
            sx={{
              minWidth: workflow.showWorkflow ? "325px" : "580px",
              flexGrow: 1,
            }}
          >
            <UTableSearch onSearch={onSearch} searchText={searchText} />
          </Grid>
          <Grid item xs={isXs ? 12 : undefined}>
            <UTableFilterList
              selectedMenu={selectedAssigners}
              menuList={assigners}
              onFilterChange={handleChange}
              listStyle={{
                minWidth: "272px",
              }}
              placeHolder={t("filter.assignedTo")}
              filterKey="assignedTo"
              label="Doctors"
            />
          </Grid>
          <Grid item xs={isXs ? 12 : undefined}>
            <UTableFilterList
              selectedMenu={selectedStatus}
              listStyle={{
                minWidth: "272px",
              }}
              menuList={status}
              onFilterChange={handleChange}
              placeHolder={t("filter.status")}
              filterKey="status"
            />
          </Grid>
          <Grid item xs={isXs ? 12 : undefined}>
            <UStatusFilter
              statusUpdatedFilter={statusUpdatedFilter}
              dateFilterOptions={STATUS_UPDATED}
              onFilterChange={(e) => handleChange(e, "updatedtime")}
              renderValue={renderStatusUpdatedValue}
            />
          </Grid>
          {workflow.showWorkflow ? (
            <Grid item xs={isXs ? 12 : undefined}>
              <UStatusFilter
                statusUpdatedFilter={workflowFilter}
                dateFilterOptions={workflow.phase.map((item) => ({
                  displayText: item.value,
                  key: item.id,
                }))}
                onFilterChange={(e) => handleChange(e, "workflow")}
                renderValue={(selected) => {
                  const selectedWorkflow = workflow.phase?.find(
                    (item) => item?.id == Number(selected),
                  )
                  return selectedWorkflow?.value || "Workflow"
                }}
              />
            </Grid>
          ) : (
            <></>
          )}
          <Grid item xs={isXs ? 12 : undefined}>
            <FormControlLabel
              sx={{
                ml: 0,
                minWidth: "275px",
                ".MuiTypography-root": {
                  fontSize: "16px",
                  color: "text.primary",
                  fontWeight: 400,
                  lineHeight: "24px",
                },
              }}
              control={
                <Checkbox
                  checked={isShowArchivedPatients}
                  onChange={handleCheckboxChange}
                />
              }
              label={t("filter.archivedPatients")}
            />
          </Grid>
        </>
      )}
      {enableSearch && name === "Org List" && (
        <Grid item sx={{ minWidth: "395px" }}>
          <UTableSearch onSearch={onSearch} searchText={searchText} />
        </Grid>
      )}
    </Grid>
  )
}

export default UTableFilter
